'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Play, Users } from 'lucide-react';

interface Webinar {
  id: string;
  title: string;
  topic: string | null;
  description: string | null;
  type: string;
  status: string;
  scheduled_at: string | null;
  teaser_url: string | null;
  full_video_url: string | null;
  views: number;
  registrations: number;
  rating: number | null;
  created_by_user_id: string | null;
}

const TOPICS = [
  'All Topics', 'Meta Ads', 'Google Ads', 'TikTok Ads', 'LinkedIn Ads',
  'Snapchat Ads', 'Reddit Ads', 'Attribution', 'Budget Strategy',
  'Agency Growth', 'General Marketing',
];

const DURATION_OPTIONS = ['Any Duration', 'Under 30 min', '30–60 min', 'Over 60 min'];

export default function WebinarsContent() {
  const { user, profile } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const supabase = createClient();

  const [activeTab, setActiveTab] = useState<'live' | 'recorded'>('live');
  const [liveWebinars, setLiveWebinars] = useState<Webinar[]>([]);
  const [recordedWebinars, setRecordedWebinars] = useState<Webinar[]>([]);
  const [loading, setLoading] = useState(true);
  const [registeringId, setRegisteringId] = useState<string | null>(null);
  const [registeredIds, setRegisteredIds] = useState<Set<string>>(new Set());
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [selectedTopic, setSelectedTopic] = useState('All Topics');
  const [searchQuery, setSearchQuery] = useState('');
  const [durationFilter, setDurationFilter] = useState('Any Duration');

  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref && typeof window !== 'undefined') {
      localStorage.setItem('incade_referral', ref);
      const expiry = Date.now() + 60 * 24 * 60 * 60 * 1000;
      localStorage.setItem('incade_referral_expiry', String(expiry));
    }
  }, [searchParams]);

  const fetchWebinars = useCallback(async () => {
    setLoading(true);
    const now = new Date().toISOString();

    const { data: live } = await supabase
      .from('webinars')
      .select('*')
      .eq('type', 'live')
      .eq('status', 'approved')
      .gt('scheduled_at', now)
      .order('scheduled_at', { ascending: true });

    const { data: recorded } = await supabase
      .from('webinars')
      .select('*')
      .eq('type', 'recorded')
      .eq('status', 'approved')
      .order('views', { ascending: false });

    setLiveWebinars(live || []);
    setRecordedWebinars(recorded || []);
    setLoading(false);
  }, [supabase]);

  useEffect(() => {
    fetchWebinars();
  }, [fetchWebinars]);

  const handleRegister = async (webinarId: string) => {
    if (!user) {
      router.push('/register?redirect=/webinars');
      return;
    }
    setRegisteringId(webinarId);
    const current = liveWebinars.find((w) => w.id === webinarId);
    await supabase
      .from('webinars')
      .update({ registrations: (current?.registrations || 0) + 1 })
      .eq('id', webinarId);
    setRegisteredIds((prev) => new Set([...prev, webinarId]));
    setRegisteringId(null);
    await fetchWebinars();
  };

  const handleWatchRecorded = (webinar: Webinar) => {
    if (!user) {
      router.push('/register?redirect=/webinars');
      return;
    }
    supabase.from('webinars').update({ views: (webinar.views || 0) + 1 }).eq('id', webinar.id);
    setPlayingId(webinar.id);
  };

  const canWatchFull = (): boolean => {
    if (!user) return false;
    const plan = profile?.plan || '';
    return ['Growth', 'Scale', 'Enterprise'].includes(plan);
  };

  const getVideoUrl = (webinar: Webinar): string => {
    if (canWatchFull()) return webinar.full_video_url || webinar.teaser_url || '';
    return webinar.teaser_url || '';
  };

  const applyFilters = useCallback((list: Webinar[]) => {
    return list.filter((w) => {
      const matchesTopic = selectedTopic === 'All Topics' || w.topic === selectedTopic;
      const q = searchQuery.toLowerCase();
      const matchesSearch = !q || w.title.toLowerCase().includes(q) || (w.description || '').toLowerCase().includes(q);
      return matchesTopic && matchesSearch;
    });
  }, [selectedTopic, searchQuery]);

  const filteredLiveWebinars = useMemo(() => applyFilters(liveWebinars), [applyFilters, liveWebinars]);
  const filteredRecordedWebinars = useMemo(() => applyFilters(recordedWebinars), [applyFilters, recordedWebinars]);

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="bg-[#1E293B] border-b border-slate-700 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2">
            <AppLogo size={28} />
            <span className="font-semibold text-white text-lg">Incade</span>
            <span className="text-orange-400 text-xs font-mono font-semibold">.io</span>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {user ? (
              <Link
                href="/dashboard"
                style={{
                  padding: '7px 18px',
                  background: 'linear-gradient(135deg, #F97316, #EA580C)',
                  color: 'white',
                  fontSize: '13px',
                  fontWeight: 700,
                  borderRadius: '8px',
                  textDecoration: 'none',
                }}
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link href="/sign-up-login-screen" className="text-sm text-slate-300 hover:text-white font-medium transition-colors">Sign in</Link>
                <Link href="/register" className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-lg transition-colors">
                  Get started free
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#1E293B] via-slate-800 to-[#1E293B] text-white overflow-hidden">
        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div>
              <h1 className="text-4xl md:text-5xl font-black leading-tight tracking-tight mb-5">
                Learn What&apos;s Actually Working in Paid Media —{' '}
                <span className="bg-gradient-to-r from-orange-400 to-orange-500 bg-clip-text text-transparent">
                  From People Spending Real Budgets
                </span>
              </h1>
              <p className="text-slate-300 text-lg leading-relaxed mb-8">
                Live sessions and recorded masterclasses from performance marketers and agencies who manage real ad spend — hosted on Incade.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setActiveTab('live')}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                    activeTab === 'live' ?'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-900/30' :'bg-orange-500/20 text-orange-300 hover:bg-orange-500/30 border border-orange-500/30'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  Upcoming Webinars
                </button>
                <button
                  onClick={() => setActiveTab('recorded')}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all ${
                    activeTab === 'recorded' ?'bg-white/15 text-white border border-white/40' :'border border-white/20 text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  On Demand
                </button>
              </div>
            </div>

            {/* Right: Video player mockup */}
            <div className="hidden md:flex justify-end">
              <div className="relative w-80">
                <div className="bg-slate-800/80 border border-slate-600/50 rounded-2xl overflow-hidden shadow-2xl">
                  {/* Player top bar */}
                  <div className="flex items-center justify-between px-4 pt-3 pb-2">
                    <span className="flex items-center gap-1.5 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                      LIVE
                    </span>
                    <div className="w-16 h-1.5 bg-slate-600 rounded-full" />
                  </div>
                  {/* Video area */}
                  <div className="relative bg-slate-900 mx-3 rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-14 h-14 bg-orange-500 rounded-full flex items-center justify-center shadow-lg shadow-orange-900/50">
                        <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-3 text-white/70 text-xs font-mono font-semibold">
                      45:00
                    </div>
                  </div>
                  {/* Skeleton content rows */}
                  <div className="px-4 py-3 space-y-2">
                    <div className="h-2 bg-slate-600/60 rounded-full w-4/5" />
                    <div className="h-2 bg-slate-600/40 rounded-full w-3/5" />
                  </div>
                  {/* Avatar row */}
                  <div className="flex items-center gap-3 px-4 pb-4">
                    <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-slate-400" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                      </svg>
                    </div>
                    <div className="space-y-1.5 flex-1">
                      <div className="h-2 bg-slate-600/60 rounded-full w-2/3" />
                      <div className="h-1.5 bg-slate-600/40 rounded-full w-1/2" />
                    </div>
                  </div>
                </div>
                {/* Decorative dots */}
                <div className="absolute -top-2 -right-2 w-3 h-3 bg-orange-400 rounded-full opacity-80" />
                <div className="absolute -bottom-3 -left-3 w-2 h-2 bg-orange-300 rounded-full opacity-60" />
                <div className="absolute top-1/2 -right-4 w-2 h-2 bg-slate-500 rounded-full opacity-50" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filter + Content */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Topic pills */}
        <div className="flex flex-wrap gap-2 mb-5">
          {TOPICS.map((topic) => (
            <button
              key={topic}
              onClick={() => setSelectedTopic(topic)}
              className={`text-sm font-medium px-4 py-1.5 rounded-full border transition-all ${
                selectedTopic === topic
                  ? 'bg-orange-500 text-white border-orange-500' :'bg-white text-slate-600 border-slate-300 hover:border-orange-400 hover:text-slate-800'
              }`}
            >
              {topic}
            </button>
          ))}
        </div>

        {/* Search + Duration */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.35-4.35" />
            </svg>
            <input
              type="text"
              placeholder="Search webinars..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 bg-white text-slate-800 placeholder-slate-400"
            />
          </div>
          <div className="relative">
            <select
              value={durationFilter}
              onChange={(e) => setDurationFilter(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2.5 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-400/40 focus:border-orange-400 bg-white text-slate-700 cursor-pointer"
            >
              {DURATION_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="m6 9 6 6 6-6" />
            </svg>
          </div>
        </div>

        {/* Section heading */}
        <h2 className="text-xl font-bold text-slate-900 mb-6">
          {activeTab === 'live' ? 'Upcoming Webinars' : 'On-Demand Library'}
        </h2>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Upcoming Live Tab */}
            {activeTab === 'live' && (
              <div className="space-y-4 pb-12">
                {filteredLiveWebinars.length === 0 ? (
                  <div className="flex flex-col items-center py-16">
                    <div className="relative mb-6">
                      {/* Decorative dots */}
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 translate-x-8 w-2.5 h-2.5 bg-orange-400 rounded-full opacity-80" />
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 translate-x-16 w-2 h-2 bg-slate-300 rounded-full" />
                      <div className="absolute top-1/2 -left-6 w-1.5 h-1.5 bg-orange-300 rounded-full opacity-70" />
                      {/* Icon card */}
                      <div className="bg-slate-100 rounded-2xl p-8 w-32 h-32 flex items-center justify-center relative">
                        <svg className="w-14 h-14 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <rect x="2" y="3" width="20" height="14" rx="2" />
                          <path d="M8 21h8M12 17v4" />
                        </svg>
                        <div className="absolute top-5 right-5 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                          <svg className="w-2.5 h-2.5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">No upcoming webinars scheduled yet</h3>
                    <p className="text-sm text-slate-500 text-center max-w-sm mb-6">
                      Check back soon for new live sessions, or explore our on-demand library for recorded masterclasses.
                    </p>
                    <button
                      onClick={() => setActiveTab('recorded')}
                      className="flex items-center gap-2 px-5 py-2.5 border border-orange-400 text-orange-600 hover:bg-orange-50 text-sm font-semibold rounded-xl transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <polygon points="5 3 19 12 5 21 5 3" />
                      </svg>
                      Watch On-Demand Library
                    </button>
                  </div>
                ) : (
                  filteredLiveWebinars.map((w) => (
                    <div key={w.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex items-center justify-between gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-semibold rounded-full">LIVE</span>
                          {w.topic && <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">{w.topic}</span>}
                        </div>
                        <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'white', marginBottom: '6px' }}>{w.title}</h3>
                        {w.description && <p style={{ fontSize: '13px', color: '#94A3B8', marginBottom: '10px', lineHeight: 1.6 }}>{w.description}</p>}
                        {w.scheduled_at && (
                          <p style={{ fontSize: '13px', color: '#64748B' }}>
                            <span style={{ fontWeight: 600, color: '#94A3B8' }}>
                              {new Date(w.scheduled_at).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                            </span>
                            {' at '}
                            {new Date(w.scheduled_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}
                          </p>
                        )}
                        <p style={{ fontSize: '12px', color: '#475569', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Users size={12} />
                          {w.registrations} registered
                        </p>
                      </div>
                      <button
                        onClick={() => handleRegister(w.id)}
                        disabled={registeringId === w.id || registeredIds.has(w.id)}
                        className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors whitespace-nowrap ${
                          registeredIds.has(w.id)
                            ? 'bg-green-100 text-green-700 cursor-default' :'bg-orange-500 hover:bg-orange-600 text-white'
                        } disabled:opacity-60`}
                      >
                        {registeringId === w.id ? 'Registering...' : registeredIds.has(w.id) ? 'Registered' : 'Register'}
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Recorded Library Tab */}
            {activeTab === 'recorded' && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-12">
                {filteredRecordedWebinars.length === 0 ? (
                  <div className="col-span-3 flex flex-col items-center py-16">
                    <div className="relative mb-6">
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 translate-x-8 w-2.5 h-2.5 bg-orange-400 rounded-full opacity-80" />
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 translate-x-16 w-2 h-2 bg-slate-300 rounded-full" />
                      <div className="absolute top-1/2 -left-6 w-1.5 h-1.5 bg-orange-300 rounded-full opacity-70" />
                      <div className="bg-slate-100 rounded-2xl p-8 w-32 h-32 flex items-center justify-center relative">
                        <svg className="w-14 h-14 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <rect x="2" y="3" width="20" height="14" rx="2" />
                          <path d="M8 21h8M12 17v4" />
                        </svg>
                        <div className="absolute top-5 right-5 w-5 h-5 bg-orange-500 rounded-full flex items-center justify-center">
                          <svg className="w-2.5 h-2.5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">No recorded masterclasses yet</h3>
                    <p className="text-sm text-slate-500 text-center max-w-sm">
                      Check back soon for expert-led sessions from performance marketers.
                    </p>
                  </div>
                ) : (
                  filteredRecordedWebinars.map((w) => {
                    const isPlaying = playingId === w.id;
                    const fullAccess = canWatchFull();
                    const videoUrl = getVideoUrl(w);

                    return (
                      <div
                        key={w.id}
                        style={{
                          background: 'rgba(255,255,255,0.02)',
                          border: '1px solid rgba(255,255,255,0.06)',
                          borderRadius: '16px',
                          overflow: 'hidden',
                          transition: 'all 0.3s ease',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(249,115,22,0.2)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.06)'; }}
                      >
                        <div style={{ position: 'relative', aspectRatio: '16/9', background: 'rgba(0,0,0,0.4)' }}>
                          {isPlaying && videoUrl ? (
                            <iframe
                              src={videoUrl}
                              style={{ width: '100%', height: '100%', border: 'none' }}
                              allow="autoplay; fullscreen"
                              allowFullScreen
                            />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <div style={{ textAlign: 'center' }}>
                                <div
                                  style={{
                                    width: '52px',
                                    height: '52px',
                                    borderRadius: '50%',
                                    background: 'rgba(249,115,22,0.15)',
                                    border: '1px solid rgba(249,115,22,0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 8px',
                                  }}
                                >
                                  <Play size={22} style={{ color: '#F97316', marginLeft: '2px' }} />
                                </div>
                                <p style={{ fontSize: '12px', color: '#64748B' }}>{w.topic || 'Webinar'}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '20px' }}>
                          {w.topic && (
                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: 700,
                                color: '#F97316',
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                display: 'block',
                                marginBottom: '8px',
                              }}
                            >
                              {w.topic}
                            </span>
                          )}
                          <h3 style={{ fontSize: '15px', fontWeight: 700, color: 'white', marginBottom: '8px', lineHeight: 1.4 }}>{w.title}</h3>
                          {w.description && (
                            <p style={{ fontSize: '13px', color: '#94A3B8', lineHeight: 1.6, marginBottom: '16px' }}>{w.description}</p>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                            <span style={{ fontSize: '12px', color: '#64748B', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Play size={11} /> {w.views} views
                            </span>
                            {!fullAccess && (
                              <span
                                style={{
                                  fontSize: '11px',
                                  fontWeight: 700,
                                  color: '#F97316',
                                  background: 'rgba(249,115,22,0.08)',
                                  border: '1px solid rgba(249,115,22,0.2)',
                                  padding: '3px 8px',
                                  borderRadius: '6px',
                                }}
                              >
                                Preview only
                              </span>
                            )}
                          </div>
                          {!isPlaying ? (
                            <button
                              onClick={() => handleWatchRecorded(w)}
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '10px',
                                fontSize: '13px',
                                fontWeight: 700,
                                border: 'none',
                                cursor: 'pointer',
                                background: 'linear-gradient(135deg, #F97316, #EA580C)',
                                color: 'white',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              {fullAccess ? 'Watch Now' : 'Watch Preview'}
                            </button>
                          ) : (
                            <button
                              onClick={() => setPlayingId(null)}
                              style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '10px',
                                fontSize: '13px',
                                fontWeight: 600,
                                border: '1px solid rgba(255,255,255,0.09)',
                                cursor: 'pointer',
                                background: 'rgba(255,255,255,0.05)',
                                color: '#CBD5E1',
                                transition: 'all 0.2s ease',
                              }}
                            >
                              Close
                            </button>
                          )}
                          {!fullAccess && !isPlaying && (
                            <Link
                              href="/pricing"
                              style={{
                                display: 'block',
                                textAlign: 'center',
                                marginTop: '8px',
                                fontSize: '12px',
                                color: '#64748B',
                                textDecoration: 'none',
                                fontWeight: 500,
                              }}
                            >
                              Upgrade for full access →
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
